import { z } from "zod";
/**
 * Languages a group can pick as the target it wants scanned receipts
 * translated into (issue #284). When a receipt is in a different language,
 * the scan pipeline produces a translated copy of the merchant/label and line
 * items in this language, surfaced alongside the original on the expense form.
 *
 * Kept deliberately small — the common languages our users settle expenses in
 * — and stored on the package (not the UI) so every client renders the same
 * label set. `code` is a BCP-47 primary subtag; `name` is the endonym +
 * English name shown in the picker. Edit this list to add a language: the zod
 * enum and the UI list both derive from it.
 */
export const SUPPORTED_LANGUAGES = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish — Español" },
    { code: "fr", name: "French — Français" },
    { code: "de", name: "German — Deutsch" },
    { code: "it", name: "Italian — Italiano" },
    { code: "pt", name: "Portuguese — Português" },
    { code: "nl", name: "Dutch — Nederlands" },
    { code: "pl", name: "Polish — Polski" },
    { code: "tr", name: "Turkish — Türkçe" },
    { code: "ru", name: "Russian — Русский" },
    { code: "ja", name: "Japanese — 日本語" },
    { code: "ko", name: "Korean — 한국어" },
    { code: "zh", name: "Chinese — 中文" },
    { code: "ar", name: "Arabic — العربية" },
    { code: "hi", name: "Hindi — हिन्दी" },
    { code: "th", name: "Thai — ไทย" },
    { code: "vi", name: "Vietnamese — Tiếng Việt" },
    { code: "id", name: "Indonesian — Bahasa Indonesia" },
];
// Cast to the literal-union tuple (not `string[]`) so the zod enum — and the
// `SupportedLanguageCode` type derived from it — stays a narrow union of the
// actual codes, matching the currency pattern. A widened `string` would make
// the type useless for picker/state typing against the generated client.
const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
/** Zod enum of the supported BCP-47 language codes. */
export const SupportedLanguageCodeSchema = z.enum(LANGUAGE_CODES);
/**
 * The default a group falls back to when it has set no explicit language —
 * the same value an older group (created before the setting existed) reads as.
 */
export const DEFAULT_GROUP_LANGUAGE = "en";
/** Human-readable name for a language code, or the code itself if unknown. */
export function languageName(code) {
    return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name ?? code;
}
